import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { House, MagnifyingGlass, FloppyDisk, MapPinLine, Trash } from "@phosphor-icons/react";

/**
 * HomeAddressCard — self-service home address widget in Réglages.
 * Once saved, the tech's `km vol d'oiseau` on /heures is computed from home
 * (not the depot). Optional: leave empty to fall back to the depot.
 */
export default function HomeAddressCard() {
  const { user, setUser } = useAuth();
  const [address, setAddress] = useState(user?.home_address || "");
  const [latitude, setLatitude] = useState(user?.home_latitude?.toString() || "");
  const [longitude, setLongitude] = useState(user?.home_longitude?.toString() || "");
  const [geocoding, setGeocoding] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setAddress(user?.home_address || "");
    setLatitude(user?.home_latitude?.toString() || "");
    setLongitude(user?.home_longitude?.toString() || "");
  }, [user?.home_address, user?.home_latitude, user?.home_longitude]);

  const hasHome = !!(user?.home_latitude && user?.home_longitude);

  const search = async () => {
    if ((address || "").trim().length < 3) {
      toast.error("Saisis au moins 3 caractères");
      return;
    }
    setGeocoding(true);
    try {
      const r = await api.get(`/geocode?q=${encodeURIComponent(address)}`);
      const results = r.data.results || [];
      if (!results.length) {
        toast.error("Aucun résultat — vérifie l'adresse");
      } else {
        setSuggestions(results);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGeocoding(false);
    }
  };

  const pick = (s) => {
    setLatitude(s.latitude.toString());
    setLongitude(s.longitude.toString());
    setAddress(s.label);
    setSuggestions([]);
  };

  const save = async () => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      toast.error("Latitude / longitude invalides");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch("/users/me/home", { address, latitude: lat, longitude: lon });
      setUser({ ...user, ...r.data });
      toast.success("Adresse domicile enregistrée");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Supprimer l'adresse domicile ? Les km repartiront du dépôt.")) return;
    setSaving(true);
    try {
      await api.patch("/users/me/home", { address: "" });
      setUser({ ...user, home_address: null, home_latitude: null, home_longitude: null });
      setAddress("");
      setLatitude("");
      setLongitude("");
      toast.success("Adresse domicile supprimée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      onClick={() => setOpen((v) => !v)}
      className="mt-3 w-full text-left rounded-2xl bg-white/[0.03] border border-white/10 p-3 active:scale-[0.99] hover:border-white/20 transition-colors"
      data-testid="home-address-card"
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${hasHome ? "bg-emerald-500/15 border-emerald-500/30" : "bg-white/5 border-white/10"}`}>
          <House size={16} weight="duotone" className={hasHome ? "text-emerald-300" : "text-white/50"} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Adresse domicile</div>
          <div className="text-[11px] text-white/50 truncate">
            {hasHome ? (address || "Coordonnées enregistrées") : "Optionnel · km calculés depuis le dépôt"}
          </div>
        </div>
        {hasHome && (
          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-300">
            Actif
          </span>
        )}
      </div>

      {open && (
        <div onClick={(e) => e.stopPropagation()} className="mt-3 space-y-2 border-t border-white/5 pt-3 cursor-default">
          <div className="text-[10px] text-white/50 leading-relaxed">
            Renseigne ton adresse pour que les km soient calculés depuis <b>ton domicile</b> vers les chantiers assignés (au lieu du dépôt Saint-Aunès).
          </div>

          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Adresse</div>
            <div className="flex gap-2">
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="12 rue de la République, 34000 Montpellier" data-testid="home-address-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} />
              <Button onClick={search} disabled={geocoding} variant="ghost" className="border border-[#005CFF]/40 text-[#7CB2FF] shrink-0" data-testid="home-geocode">
                <MagnifyingGlass size={13} className={geocoding ? "animate-spin" : ""} />
              </Button>
            </div>
          </label>

          {suggestions.length > 0 && (
            <div className="rounded-xl bg-black/40 border border-white/10 divide-y divide-white/5" data-testid="home-suggestions">
              {suggestions.map((s, i) => (
                <button key={`${s.latitude}-${s.longitude}-${i}`} onClick={() => pick(s)} className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 text-xs">
                  <MapPinLine size={12} className="text-[#7CB2FF] shrink-0" weight="fill" />
                  <div className="flex-1 truncate">{s.label}</div>
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Latitude</div>
              <Input type="number" step="0.0001" value={latitude} onChange={(e) => setLatitude(e.target.value)} data-testid="home-lat" />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Longitude</div>
              <Input type="number" step="0.0001" value={longitude} onChange={(e) => setLongitude(e.target.value)} data-testid="home-lng" />
            </label>
          </div>

          <div className="flex gap-2 pt-1">
            {hasHome && (
              <Button onClick={clear} disabled={saving} variant="ghost" className="border border-red-500/30 text-red-300 hover:bg-red-500/10 gap-1.5" data-testid="home-clear">
                <Trash size={12} /> Supprimer
              </Button>
            )}
            <div className="flex-1" />
            <Button onClick={save} disabled={saving} className="bg-[#005CFF] hover:bg-[#0047CC] gap-1.5" data-testid="home-save">
              <FloppyDisk size={13} weight="fill" /> Enregistrer
            </Button>
          </div>
        </div>
      )}
    </button>
  );
}
